import * as Sentry from "@sentry/react"
import type { StripeError } from "@stripe/stripe-js"

type Scope =
  | "guard"
  | "auth"
  | "validation"
  | "fetchRequest"
  | "fetchResponse"
  | "payment"
  | "misconfiguration"
  | "info"

export type Flow =
  | "gated"
  | "gated:register"
  | "gated:exit"
  | "park"
  | "park:extend"
  | "passes"
  | "passes:purchase"
  | "passes:redeem"
  | "reserve"
  | "misc"
type LogLevel = "debug" | "log" | "info" | "warn" | "error"

const sentryLevelMap: Record<LogLevel, Sentry.SeverityLevel> = {
  debug: "debug",
  log: "info",
  info: "info",
  warn: "warning",
  error: "error",
}

type SentryMode = "breadcrumb" | "exception" | "none"

type LogParams = {
  level: LogLevel
  scope: Scope | `${Scope}.${string}`
  flow: Flow
  message: string
  meta?: Record<string, unknown>
  style?: string
  group?: {
    title: string
  }
  /**
   * How this log reaches Sentry. Defaults to "breadcrumb".
   *
   * API failReason errors are already captured as exceptions by the fetch++
   * wrapper, so re-capturing them here would double-count. The convention at
   * call sites is to send expected errors as breadcrumbs and only
   * escalate unexpected/network errors to an exception:
   *   sentry: e instanceof ExpectedError ? "breadcrumb" : "exception"
   */
  sentry?: SentryMode
  exception?: Error | StripeError
}

export function log({
  level,
  scope,
  flow,
  message,
  meta,
  group,
  style,
  sentry = "breadcrumb",
  exception,
}: LogParams) {
  const category = `${flow}.${scope}`

  if (group && meta) {
    if (style) {
      console.groupCollapsed(`%c${group.title}`, style)
    } else {
      console.groupCollapsed(group.title)
    }

    Object.entries(meta).forEach(([key, value]) => {
      console.log(`${key}:`, value)
    })
    console.groupEnd()
  } else {
    if (style) {
      console[level](`%c[${category}] ${message}`, style, meta)
    } else {
      console[level](`[${category}] ${message}`, meta)
    }
  }

  // Add breadcrumb to Sentry
  if (sentry === "breadcrumb") {
    Sentry.addBreadcrumb({
      category,
      message,
      level: sentryLevelMap[level],
      data: meta,
    })
  }

  // Capture exception in Sentry
  if (sentry === "exception" && exception) {
    Sentry.captureException(exception, {
      extra: meta,
      tags: {
        section: category,
        flow,
      },
    })
  }
}
